import mapboxgl from 'mapbox-gl';
import { RevenueCatWrapper } from '../../utils/RevenueCatWrapper';
import { peak3dStyleUrl } from './peak3dStyle';

declare global {
  interface Window {
    PEAK_COORDS: [number, number];
    PEAK_NAME: string;
    PEAK_ELEV: number;
  }
}

const MAPBOX_TOKEN = 'pk.eyJ1IjoiaGlpa2VyLW1hcHMiLCJhIjoiY2x0YThyZHppMWhmejJqbzExODBuY3dveiJ9.nBrtnXgbzxpAx_953HCB-w';

const TERRAIN_SOURCE_DEF: mapboxgl.RasterDemSourceSpecification = {
  type: 'raster-dem',
  tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
  encoding: 'terrarium',
  tileSize: 256,
  maxzoom: 13,
};

let currentExaggeration = 1.2;
let isPlaying = true;
let hasStarted = false;
let speedDeg = 0.08;
let animFrame: number | null = null;
let bearing = 20;
let map: mapboxgl.Map;

function applyTerrain() {
  try {
    if (!map.getSource('terrain')) {
      map.addSource('terrain', TERRAIN_SOURCE_DEF);
    }
    map.setTerrain({ source: 'terrain', exaggeration: currentExaggeration });
  } catch (e) {
    console.warn('Terrain apply failed:', e);
  }
}

function applyFog() {
  try {
    map.setFog({
      range: [0.5, 6],
      color: 'rgba(20, 24, 38, 0.85)',
      'horizon-blend': 0.12,
      'high-color': 'rgb(36, 60, 120)',
      'space-color': '#0a0b10',
      'star-intensity': 0.3,
    } as any);
  } catch (e) {
    console.warn('Fog apply failed:', e);
  }
}

function applySkyLayer() {
  try {
    if (map.getLayer('peak3d-sky')) return;
    map.addLayer({
      id: 'peak3d-sky',
      type: 'sky',
      paint: {
        'sky-type': 'atmosphere',
        'sky-atmosphere-sun': [0, 90],
        'sky-atmosphere-sun-intensity': 3,
        'sky-atmosphere-color': 'rgba(25, 28, 48, 1)',
        'sky-atmosphere-halo-color': 'rgba(50, 60, 110, 0.4)',
        'sky-atmosphere-space-color': '#0a0b10',
        'sky-opacity': ['interpolate', ['linear'], ['zoom'], 0, 0, 6, 0.5, 10, 1],
      },
    } as any);
  } catch (e) {
    console.warn('Sky layer apply failed:', e);
  }
}

function startRotation() {
  if (animFrame) return;
  bearing = map.getBearing();
  const frame = () => {
    if (!isPlaying) { animFrame = null; return; }
    bearing = (bearing + speedDeg) % 360;
    map.setBearing(bearing);
    updateBearingUI();
    animFrame = requestAnimationFrame(frame);
  };
  frame();
}

function stopRotation() {
  if (animFrame) { cancelAnimationFrame(animFrame); animFrame = null; }
}

const PX_PER_DEG = 2;
let rotateTicksEl: HTMLElement | null = null;
let rotateTrackEl: HTMLElement | null = null;
let bearingLabelEl: HTMLElement | null = null;

function updateBearingUI() {
  if (rotateTicksEl && rotateTrackEl) {
    const center = rotateTrackEl.clientWidth / 2;
    rotateTicksEl.style.backgroundPositionX = `${center - bearing * PX_PER_DEG}px, ${center - bearing * PX_PER_DEG}px`;
  }
  if (bearingLabelEl) {
    bearingLabelEl.textContent = `${Math.round(((bearing % 360) + 360) % 360)}°`;
  }
}

document.addEventListener('DOMContentLoaded', async () => {
  const PEAK   = window.PEAK_COORDS;
  const name   = window.PEAK_NAME;
  const elev   = window.PEAK_ELEV;

  // Populate stat cards
  const nameEl = document.getElementById('peak-3d-name');
  if (nameEl) nameEl.textContent = name;

  const elevEl = document.getElementById('peak-3d-elev');
  if (elevEl) elevEl.innerHTML = elev > 0 ? `${elev.toLocaleString()}<span class="peak3d-stat__unit"> m</span>` : '—';

  const latEl = document.getElementById('peak-3d-lat');
  if (latEl) latEl.innerHTML = `${Math.abs(PEAK[1]).toFixed(2)}<span class="peak3d-stat__unit">°${PEAK[1] >= 0 ? 'N' : 'S'}</span>`;

  const lonEl = document.getElementById('peak-3d-lon');
  if (lonEl) lonEl.innerHTML = `${Math.abs(PEAK[0]).toFixed(2)}<span class="peak3d-stat__unit">°${PEAK[0] >= 0 ? 'E' : 'W'}</span>`;

  // Loader animation
  const loaderFill = document.getElementById('peak-3d-loader-fill');
  let loadPct = 0;
  const loadInterval = setInterval(() => {
    loadPct = Math.min(loadPct + Math.random() * 8, 90);
    if (loaderFill) loaderFill.style.width = `${loadPct}%`;
  }, 120);

  // Map styles — pro-aware, so a saved Pro style (Hiking+, National+, …)
  // isn't silently demoted to Standard for entitled users.
  const isPro = await RevenueCatWrapper.isProOrProPlus();

  // Mapbox init
  mapboxgl.accessToken = MAPBOX_TOKEN;

  map = new mapboxgl.Map({
    container: 'peak-3d-map',
    style: peak3dStyleUrl(isPro),
    center: PEAK,
    zoom: 12,
    pitch: 45,
    bearing: -10,
    minZoom: 11,
    maxZoom: 14,
    maxPitch: 65,
    maxBounds: [
      [PEAK[0] - 0.2, PEAK[1] - 0.15],
      [PEAK[0] + 0.2, PEAK[1] + 0.15],
    ],
    renderWorldCopies: false,
    interactive: false,
    antialias: true,
    attributionControl: { compact: true },
  });
  (window as any).map = map;

  // Re-apply terrain, sky and rotation after any style swap
  map.on('style.load', () => {
    applyTerrain();
    applySkyLayer();
    applyFog();
    if (isPlaying && hasStarted) {
      stopRotation();
      startRotation();
    }
  });

  map.on('load', () => {
    applyTerrain();
    applySkyLayer();
    applyFog();

    // If the peak record has no elevation, fall back to the DEM at its
    // coordinate once terrain tiles are loaded.
    if (!elev || elev <= 0) {
      const elevEl2 = document.getElementById('peak-3d-elev');
      const tryQueryTerrain = () => {
        const queried = (map as any).queryTerrainElevation?.(PEAK);
        if (queried && queried > 0) {
          const rounded = Math.round(queried);
          window.PEAK_ELEV = rounded;
          if (elevEl2) elevEl2.innerHTML = `${rounded.toLocaleString()}<span class="peak3d-stat__unit"> m</span>`;
          map.off('idle', tryQueryTerrain);
        }
      };
      map.on('idle', tryQueryTerrain);
    }

    clearInterval(loadInterval);
    if (loaderFill) loaderFill.style.width = '100%';

    setTimeout(() => {
      document.getElementById('peak-3d-loader')?.classList.add('peak3d-loader--hidden');
      document.getElementById('peak-3d-title')?.classList.add('peak3d-title--visible');
      document.getElementById('peak-3d-stats')?.classList.add('peak3d-stats--visible');
      document.getElementById('peak-3d-controls')?.classList.add('peak3d-controls--visible');
      document.getElementById('peak-3d-rotate')?.classList.add('peak3d-rotate--visible');
    }, 500);

    // Summit beacon
    const markerEl = document.createElement('div');
    markerEl.className = 'p3d-beacon';
    markerEl.innerHTML = `
      <div class="p3d-beacon__beam"></div>
      <div class="p3d-beacon__halo"></div>
      <div class="p3d-beacon__ring"></div>
      <div class="p3d-beacon__core"></div>
    `;
    new mapboxgl.Marker({ element: markerEl, anchor: 'bottom' }).setLngLat(PEAK).addTo(map);

    map.flyTo({
      center: PEAK,
      zoom: 13,
      pitch: 60,
      bearing: 20,
      duration: 2000,
      easing: (t: number) => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
    });

    setTimeout(() => {
      hasStarted = true;
      bearing = map.getBearing();
      updateBearingUI();
      if (isPlaying) startRotation();
    }, 2500);
  });

  // ── Rotation drag widget ────────────────────────────────────────────────
  rotateTrackEl  = document.getElementById('peak-3d-rotate-track');
  rotateTicksEl  = document.getElementById('peak-3d-rotate-ticks');
  bearingLabelEl = document.getElementById('peak-3d-bearing');
  updateBearingUI();

  if (rotateTrackEl) {
    let dragStartX = 0;
    let dragStartBearing = 0;
    let dragging = false;
    let wasPlaying = false;

    rotateTrackEl.addEventListener('pointerdown', (e: PointerEvent) => {
      if (!hasStarted) return; // ignore drags during the intro flyTo
      dragging = true;
      dragStartX = e.clientX;
      dragStartBearing = map.getBearing();
      bearing = dragStartBearing;
      wasPlaying = isPlaying;
      stopRotation();
      try { rotateTrackEl!.setPointerCapture(e.pointerId); } catch { /* noop */ }
      e.preventDefault();
    });

    rotateTrackEl.addEventListener('pointermove', (e: PointerEvent) => {
      if (!dragging) return;
      const delta = e.clientX - dragStartX;
      // Drag right → bearing decreases (rotate camera left), drag left → bearing increases
      bearing = (((dragStartBearing - delta / PX_PER_DEG) % 360) + 360) % 360;
      map.setBearing(bearing);
      updateBearingUI();
    });

    const endDrag = (e: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      try { rotateTrackEl!.releasePointerCapture(e.pointerId); } catch { /* noop */ }
      if (wasPlaying && hasStarted) startRotation();
    };
    rotateTrackEl.addEventListener('pointerup',     endDrag);
    rotateTrackEl.addEventListener('pointercancel', endDrag);
  }

  // Controls
  document.getElementById('peak-3d-play')?.addEventListener('click', () => {
    isPlaying = !isPlaying;
    document.getElementById('peak-3d-icon-pause')!.style.display = isPlaying ? 'block' : 'none';
    document.getElementById('peak-3d-icon-play')!.style.display  = isPlaying ? 'none'  : 'block';
    if (isPlaying && hasStarted) startRotation();
    else stopRotation();
  });

  (document.getElementById('peak-3d-speed') as HTMLInputElement)?.addEventListener('input', e => {
    speedDeg = parseFloat((e.target as HTMLInputElement).value);
  });

  (document.getElementById('peak-3d-pitch') as HTMLInputElement)?.addEventListener('input', e => {
    map.setPitch(parseFloat((e.target as HTMLInputElement).value));
  });

  (document.getElementById('peak-3d-zoom') as HTMLInputElement)?.addEventListener('input', e => {
    map.setZoom(parseFloat((e.target as HTMLInputElement).value));
  });

  (document.getElementById('peak-3d-exaggeration') as HTMLInputElement)?.addEventListener('input', e => {
    currentExaggeration = parseFloat((e.target as HTMLInputElement).value);
    applyTerrain();
  });
});
